import useClientAttachmentValidator from "hooks/attachments/useClientAttachmentValidator";
import { OnErrorSignature, useCentralApi, useCurrentUser } from "hooks/shared";
import { FileOrLink, AttachmentValidationMode, ValidateAttachmentSignature, ValidateAttachmentArguments, AttachmentValidationWorkingState, checkMode, IAttachmentValidator } from "models/attachments";
import { GuidOrNumber } from "models/common";

import { useState } from "react";

type UseTaskAttachmentValidationProps = {
    taskId?: number;
    attachmentId?: GuidOrNumber;
    typeId?: number;
    fileOrLink?: FileOrLink;
};

type UseTaskAttachmentValidationSignature = (
    props: UseTaskAttachmentValidationProps
) => IAttachmentValidator;

const useTaskAttachmentValidation: UseTaskAttachmentValidationSignature = (props) => {
    const {
        taskId, 
        attachmentId,
        typeId,
        fileOrLink
    } = props;

    const clientValidator = useClientAttachmentValidator({ fileOrLink });
    const [ isValid, setIsValid ] = useState<boolean>(false);
    const [ working, setWorking ] = useState<AttachmentValidationWorkingState>({
        mode: 'full',
        working: false
    });
    const [ summary, setSummary ] = useState<any>({});

    const user = useCurrentUser();
    const fetcher = useCentralApi(user);
    
    const handleError = async (resp: Response, mode: AttachmentValidationMode) => {
        if (resp.status === 400) {
            setIsValid(false);
            const body = await resp.json();
            const newSummary = body.errors;
            clientValidator.clearByMode(summary, newSummary, mode);
            setSummary(newSummary);
            return true;
        }
        return false;
    };

    const validate = async (mode: AttachmentValidationMode, data: ValidateAttachmentArguments) => {
        const { name, url, filenames } = data;

        setWorking({
            mode,
            working: true
        });

        // Client-side validations
        const [ isValid, newSummary ] = clientValidator.validate(mode, data);

        clientValidator.clearByMode(summary, newSummary, mode);

        setSummary(newSummary);
        setIsValid(isValid);

        if (!clientValidator.canContinue(newSummary, mode, isValid)) {
            setWorking({
                mode,
                working: false
            });
            return isValid;
        }
        
        if (user && user.access_token) {
            var params = new URLSearchParams();
            
            if (name) 
                params.append('name', name);
            if (url)
                params.append('url', url);
            if (filenames)
                params.append('filenames', filenames.join('|'));
            if (typeId) 
                params.append('type', typeId.toString());
            if (fileOrLink) 
                params.append('fileOrLink', fileOrLink.toString());
            if (attachmentId)
                params.append('id', attachmentId.toString());
            if (fileOrLink === FileOrLink.SFTP)
                params.append('dup-check', 'false');

            const query = `full?${params}`;

            try {
                const resp = await fetcher(
                    `/api/v3/tasks/${taskId}/attachments/validate/${query}`, {}, 
                    (r) => handleError(r, mode)
                );

                if (resp.ok) {
                    setIsValid(isValid);
                    if (mode === 'full')
                        setSummary({});
                }

                return isValid && resp.ok;
            } finally {
                setWorking({
                    mode,
                    working: false
                });
            }
        }

        setWorking({
            mode,
            working: false
        });
        setIsValid(false);
        return false;
    };

    return {
        isValid,
        state: working,
        validate,
        reset: () => setSummary({}),
        summary
    };
};

export default useTaskAttachmentValidation;